import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Grid, Card, Toolbar, CardContent } from '@mui/material';
import Moment from 'react-moment';
import ContentLoader from '../../components/content_loader';
import Helpers from '../../helpers';
import Config from '../../config';
import { ErrorOutline as LoadErrorIcon } from '@mui/icons-material';
import {TransferTable} from './transfers';
import {FakeField} from '../../components/text_field';
import { Link } from 'react-router-dom';
import axios from 'axios';

interface TransferDetailsProps {
    setTitle?: (title: string) => void;
    [key: string]: any;
}

interface Transfer {
    Id?: number | string;
    Status?: string;
    statusCode?: number;
    PaymentType?: string;
    Partner?: string;
    PartnerId?: number | string;
    ExternalId?: string;
    PayoutId?: number | string;
    MarketplaceOrderId?: number | string;
    OrderId?: number | string;
    Line?: number | string;
    TransferAmount?: number;
    Currency?: string;
    Notes?: string;
    Created?: string;
}

interface TransferDetailsState {
    loaded?: boolean;
    transfer?: Transfer | null;
    transferId?: string;
    loadError?: string;
}

const TransferDetails = (props: TransferDetailsProps) => {
    const { setTitle } = props;
    const [loaded, setLoaded] = useState<TransferDetailsState['loaded']>(undefined);
    const [transfer, setTransfer] = useState<TransferDetailsState['transfer']>(undefined);
    const [transferId, setTransferId] = useState<TransferDetailsState['transferId']>(undefined);
    const [loadError, setLoadError] = useState<TransferDetailsState['loadError']>(undefined);

    // componentDidMount: set the page title once on mount.
    useEffect(() => {
        if (setTitle) {
            setTitle('Payment Center');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // componentDidMount + componentDidUpdate both invoked tryLoad; running this
    // effect after every render reproduces that, and the transferId guard keeps
    // it from re-fetching until the id in the URL actually changes.
    useEffect(() => {
        const tryLoad = async () => {
            try {
                let currentTransferId = new Helpers().queryString()['id'];
                if (transferId === currentTransferId) {
                    return;
                }
                setLoaded(false);
                setTransfer(null);
                setTransferId(currentTransferId);
                let loadedTransfer: Transfer = (await axios.get(Config.api + "/odata/Company/PaymentRecords?$filter=Id eq " + currentTransferId)).data.value[0];
                if (loadedTransfer.Status === "Failed") {
                    loadedTransfer.statusCode = 1200;
                } else if (loadedTransfer.Status === "Ignored") {
                    loadedTransfer.statusCode = 1400;
                } else if (loadedTransfer.Status === "Complete") {
                    loadedTransfer.statusCode = 1000;
                }
                setTransfer(loadedTransfer);
                setLoaded(true);
            } catch {
                // Restore the original atomic setState({loaded, loadError}): under legacy
                // ReactDOM.render (no async batching) flipping loaded before loadError let
                // an intermediate render pass the loadError early-return with transfer still
                // null, crashing on transfer.PaymentType. Commit both together.
                unstable_batchedUpdates(() => {
                    setLoaded(true);
                    setLoadError('Failed to load transfer details.');
                });
            }
        };
        tryLoad();
    });

    if (loadError) {
        return <Grid container spacing={2}>
                <Grid size={12}>
                  <LoadErrorIcon style={{fontSize: '14rem', color: '#999'}}/>
                </Grid>
                <Grid size={12} style={{fontSize: '1.5rem', color: '#999'}}>{loadError}</Grid>
            </Grid>;
    }
    let transferFilter: any = null;
    if(transfer){
        if(transfer.PaymentType === 'Payout'){
            transferFilter = {operator: 'eq', field: 'PayoutId', value: transfer.Id.toString()};
        } else {
            transferFilter = {filters: [
                {operator: 'eq', field: 'MarketplaceOrderId', value: transfer.MarketplaceOrderId.toString()},
                {operator: 'eq', field: 'PartnerId', value: transfer.PartnerId}
            ], logic: 'and'};
        }
    }
    let currencyFormat = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: ((transfer || {}).Currency) || 'USD',
      minimumFractionDigits: 2
    }).format;
    return <Grid container spacing={2}>
        <Grid size={12}>
            {loaded ? <Card>
                  <Toolbar className='lbdocbar' style={{display: 'flex'}}>
                      <div style={{flex: 1, textAlign: 'left'}}>{transfer.PaymentType} #{transfer.Id} ({transfer.Partner})</div>
                  </Toolbar>
                  {/* statuscode is a custom attribute (styled via App.css's .statusColor[statuscode=...]
                      selectors), not a real Toolbar prop, so the object is spread as `any` to
                      bypass MUI's strict OverridableComponent typing for it. */}
                  <Toolbar className='lbstatusbar statusColor' {...({statuscode: transfer.statusCode} as any)}>Status: {transfer.Status}<span style={{right: '24px', position: 'absolute'}}>Created: <Moment format='MM/DD/YYYY h:mm A'>{transfer.Created}</Moment> </span></Toolbar>
              </Card> : <ContentLoader preserveAspectRatio='none' style={{height: '7em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
            {loaded ? <Card style={{flex: 1}}>
                  <Toolbar className='lbtoolbar' style={{display: 'flex'}}>Identifiers</Toolbar>
                  <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                    <FakeField label="Logicbroker ID">{transfer.Id}</FakeField>
                    <FakeField label="External ID">{transfer.ExternalId}</FakeField>
                    <FakeField label="Payout ID">{transfer.PayoutId && <Link to={`/payments/transfer?id=${transfer.PayoutId}`}>{transfer.PayoutId}</Link>}</FakeField>
                    <FakeField label="Marketplace Order">{transfer.MarketplaceOrderId && <Link to={`/search?term=${transfer.MarketplaceOrderId}`}>{transfer.MarketplaceOrderId}</Link>}</FakeField>
                    <FakeField label="Purchase Order">{transfer.OrderId && <Link to={`/order-management/order-details?orderid=${transfer.OrderId}`}>{transfer.OrderId}</Link>}</FakeField>
                    <FakeField label="Line Number">{transfer.Line}</FakeField>
                  </CardContent>
              </Card> : <ContentLoader preserveAspectRatio='none' style={{height: '20em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
            {loaded ? <Card style={{flex: 1}}>
                  <Toolbar className='lbtoolbar' style={{display: 'flex'}}>Transfer Details</Toolbar>
                  <CardContent style={{display: 'flex', flexDirection: 'column'}}>
                    <FakeField label="Type">{transfer.PaymentType}</FakeField>
                    <FakeField label="Amount">{currencyFormat(transfer.TransferAmount)}</FakeField>
                    <FakeField label="Currency">{transfer.Currency}</FakeField>
                  </CardContent>
              </Card> : <ContentLoader preserveAspectRatio='none' style={{height: '20em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }} style={{display: 'flex'}}>
            {loaded ? <Card style={{flex: 1}}>
                  <Toolbar className='lbtoolbar' style={{display: 'flex'}}>Notes</Toolbar>
                  <CardContent style={{display: 'flex', flexDirection: 'column', minHeight: '5em', wordBreak: 'break-word', textAlign: 'left', whiteSpace: 'pre-wrap'}}>
                  {transfer.Notes}
                  </CardContent>
              </Card> : <ContentLoader preserveAspectRatio='none' style={{height: '20em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={12}>
            {loaded ? <TransferTable filter={transferFilter} title='Related Transfers'/> : <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        </Grid>;
}

export default TransferDetails;
